import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert as MuiAlert,
  Divider,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SchoolIcon from "@mui/icons-material/School";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { apiService } from "../../services/api";

const ROLE_COLOR = {
  Provider: "success",
  Researcher: "info",
  Patient: "primary",
};

function KpiCard({ icon, label, value, color = "primary.main" }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: `${color}15`,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function daysPending(createdAt) {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;
  const diffMs = Date.now() - created.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

// Shows only the most recent handful here — this is a preview, not a
// replacement for the full approval workflow. Every row and the "Review
// All Approvals" button both go straight to /admin/user-verification,
// where the actual approve/reject actions live.
const PREVIEW_COUNT = 5;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingPracticeChanges, setPendingPracticeChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [users, practiceChanges] = await Promise.all([
          apiService.getPendingUsers(),
          apiService.getPendingPracticeChanges(),
        ]);
        if (!cancelled) {
          setPendingUsers(users || []);
          setPendingPracticeChanges(practiceChanges || []);
        }
      } catch (err) {
        console.error("Failed to load admin dashboard:", err);
        if (!cancelled) setError("Failed to load pending approvals. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Container sx={{ mt: 6, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading dashboard…
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <MuiAlert severity="error">{error}</MuiAlert>
      </Container>
    );
  }

  const pendingProviders = pendingUsers.filter((u) => u.metadata?.role === "Provider");
  const pendingResearchers = pendingUsers.filter((u) => u.metadata?.role === "Researcher");
  const previewUsers = pendingUsers.slice(0, PREVIEW_COUNT);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Dashboard
          </Typography>
          <Typography color="text.secondary">
            Provider and Researcher signups waiting on your review
          </Typography>
        </Box>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate("/admin/user-verification")}
        >
          Review All Approvals
        </Button>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            icon={<PersonAddAlt1Icon />}
            label="Pending Providers"
            value={pendingProviders.length}
            color="#1A5EA8"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            icon={<SchoolIcon />}
            label="Pending Researchers"
            value={pendingResearchers.length}
            color="#0284C7"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            icon={<BusinessIcon />}
            label="Pending Practice Changes"
            value={pendingPracticeChanges.length}
            color="#D97706"
          />
        </Grid>
      </Grid>

      {/* Recent pending approvals preview */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Recent Pending Approvals
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {pendingUsers.length > PREVIEW_COUNT
            ? `Showing the ${PREVIEW_COUNT} most recent of ${pendingUsers.length} — review all on the full approval page.`
            : "Click a row, or \"Review All Approvals\" above, to approve or reject."}
        </Typography>

        {previewUsers.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">
              No pending Provider or Researcher signups right now.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {previewUsers.map((user, idx) => {
              const pending = daysPending(user.createdAt);
              return (
                <Box key={user.userId ?? idx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 1.5,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                      borderRadius: 1,
                      px: 1,
                    }}
                    onClick={() => navigate("/admin/user-verification")}
                  >
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography fontWeight={600}>{user.name}</Typography>
                        <Chip
                          size="small"
                          label={user.metadata?.role}
                          color={ROLE_COLOR[user.metadata?.role] || "default"}
                        />
                        {user.metadata?.role === "Provider" && user.metadata?.practiceName && (
                          <Chip size="small" variant="outlined" label={user.metadata.practiceName} />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                        {pending !== null ? ` — pending ${pending} day${pending === 1 ? "" : "s"}` : ""}
                      </Typography>
                    </Box>
                    <Button size="small" variant="text">
                      Review
                    </Button>
                  </Box>
                  {idx < previewUsers.length - 1 && <Divider />}
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    </Container>
  );
}